import React, { useEffect, useState } from "react";
import { AdminPageTemplate, ColumnToolTip, ModalCallSummary, } from "../../features";
import { useOngoingCalls } from "../hooks/useOngoingCalls";
import { generateClient } from 'aws-amplify/api';
import * as Subscriptions from '../../graphql/subscriptions';

import LoadingBar from "react-top-loading-bar";
import { AppRouteUi, CallCenterActivityTypeOptions, ExportTableType, PaginationSettings, topLoaderProgress, } from "../../configs";
import { useFunctionalityCallCenterStatus } from "../hooks/functionalityCallCenterStatus";
import { useAppSelector, VRIAppStateType } from "../../core";
import { ColumnSelectionsModel, TableColumnExtend } from "../../models";
import { CallCenterOngoingCallsColumns } from "../../configs/AppJson/CallCenterOngoingCallsColumns";
import moment from "moment";
import { ApiSchemaFilter } from "../../models/api/filter";
import { useTopLoader } from "../hooks/useTopLoader";
import { OngoingCall } from "../../models/amplify/API";
import { formatPhoneNumber } from "../../utils/phoneNumberFormatting";
import { OngoingCallsAccordionRow } from "../../components/CallCenter/OngoingCallsAccordionRow";
import { useLiveHHMMSSFrom } from "../hooks/useLiveHHMMSSFrom";
import DataTable from "react-data-table-component";
import { NoDataComponent } from "../../features/admin/noDataComponent";
import type { VriOngoingView } from "../../models/mappers/ongoingCall";

export function PageOngoingCalls() {
  const callCenterId = useAppSelector(
    (state: VRIAppStateType) => state.auth.menuSettings?.callCenterSettings.callCenterId
  );
  const [isShowCommonFilter, setIsShowCommonFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  // const [pageIndex, setPageIndex] = useState(PaginationSettings.initialPageIndex);
  const [pageSize, setPageSize] = useState(1000);
  const [hasPagingData, setHasPagingData] = useState<boolean>(false);
  const [disablePaging, setDisablePaging] = useState<boolean>(true);
  const [columnSelection, setColumnSelection] = useState<ColumnSelectionsModel[]>(CallCenterOngoingCallsColumns);
  const [columnSelectionModalShow, setColumnSelectionModalShow] = React.useState(false);

  let initialFilterData: ApiSchemaFilter = {
    filterQueries: [],
    sortQuery: {},
  };
  const [filterData, setFilterData] = useState<ApiSchemaFilter>(initialFilterData);
  const [filterDataChange, setFilterDataChange] = useState<number>(0);
  const [fetchIsStale, setFetchIsStale] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState("No ongoing calls.");

  // const { calls, loading, err, refresh, trigger, pageSize, setPageSize, page, nextPage, prevPage, isLastPage, setPage } = useOngoingCalls({});
    const { opiCalls, vriCalls, loading, err, refresh, trigger, page, nextPage, prevPage, isLastPage, setPage } = useOngoingCalls({});

  const vriEmptyMessage = loading
    ? "Loading..."
    : err
      ? "Unable to retrieve data."
      : "No ongoing video calls.";


  useEffect(() => {
    setEmptyMessage(err ? "Unable to retrieve data." : "No ongoing calls.");
    console.error(err);
  }, [err]);


  const { progress, handleFinished } = useTopLoader(loading);

  useEffect(() => {
    console.log("calls", opiCalls);
  }, [opiCalls])

  const LiveDurationCell: React.FC<{ startMs?: number }> = ({ startMs }) => {
  const text = useLiveHHMMSSFrom(startMs); 
  return <ColumnToolTip data={text} />;
};

  const formatDurationMs = (ms?: number | null) => {
    if (!ms || ms <= 0) return "-";
    return moment.utc(ms).format("HH:mm:ss");
  };

  const ongoingCallsTableColumns: TableColumnExtend<OngoingCall>[] =
    [
      {
        name: <ColumnToolTip data="Call Id" />,
        sortable: false,
        sortField: "Actor.Name",
        selector: (row: OngoingCall) => {
          return (
            <ColumnToolTip data={row.callId ?? ""} />
          );
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "CallId")?.isEnabled ? false : true,
      },
      {
        name: <ColumnToolTip data="Call SId" />,
        sortable: false,
        sortField: "Actor.Name",
        selector: (row: OngoingCall) => {
          return (
            <ColumnToolTip data={row.callSid ?? ""} />
          );
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "CallSid")?.isEnabled ? false : true,
      },
      {
        name: <ColumnToolTip data="Call Status" />,
        width: "200px",
        sortable: false,
        selector: (row: OngoingCall) => {
          return (
            <div className={`colored-ongoingCalls status-${row.callStatus}`}>
              {row.callStatus}
            </div>
          )
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "CallStatus")?.isEnabled ? false : true,
      },
      {
        name: <ColumnToolTip data="Client Number" />,
        sortable: false,
        selector: (row: OngoingCall) => {
          return <ColumnToolTip data={row.clientNumber ? formatPhoneNumber(row.clientNumber) : "-"} />;
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "ClientNumber")?.isEnabled ? false : true,
      },
            {
        name: <ColumnToolTip data="Pin" />,
        sortable: false,
        selector: (row: OngoingCall) => {
          return <ColumnToolTip data={row.pin ?? "-"} />;
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "Pin")?.isEnabled ? false : true,
      },

      {
        name: <ColumnToolTip data="Language Name" />,
        sortable: false,
        sortField: "Language",
        selector: (row: OngoingCall) => {
          return <ColumnToolTip data={row.languageName ?? "-"} />;
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "LanguageName")?.isEnabled ? false : true,
      },
      {
        name: <ColumnToolTip data="Start Time" />,
        sortable: true,
        sortField: "StartTime",
        selector: (row: OngoingCall) => {
          const date = row.callStartedAt ? row.callStartedAt : null;

          return (
            <ColumnToolTip
              data={date ? moment(Number(date)).format("HH:mm:ss") : "-"}
            />
          );
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "CallStartedAt")?.isEnabled ? false : true,
      },
      {
        name: <ColumnToolTip data="Duration" />,
        sortable: true,
        sortField: "StartTime",
        selector: (row: OngoingCall) => {

          return (
            <LiveDurationCell startMs={row.callStartedAt ? Number(row.callStartedAt) : undefined} />
          );
        },
        omit: columnSelection?.find((eachColumn) => eachColumn.propertyName === "CallStartedAt")?.isEnabled ? false : true,
      },

    ];

  const vriTableColumns: TableColumnExtend<VriOngoingView>[] = [
    {
      name: <ColumnToolTip data="Room Sid" />,
      sortable: false,
      width: "165px",
      minWidth: "165px",
      selector: (row: VriOngoingView) => <ColumnToolTip data={row.roomSid ?? ""} />,
    },
    {
      name: <ColumnToolTip data="Call Id" />,
      sortable: false,
      width: "150px",
      minWidth: "150px",
      selector: (row: VriOngoingView) => <ColumnToolTip data={row.callId ?? "-"} />,
    },
    {
      name: <ColumnToolTip data="Room Status" />,
      sortable: false,
      width: "140px",
      minWidth: "140px",
      maxWidth: "140px",
      selector: (row: VriOngoingView) => {
        const raw = row.roomStatus ?? "-";
        const text = raw === "-" ? "-" : String(raw).toUpperCase();
        const isInProgress = String(raw).toLowerCase() === "in-progress";
        return (
          <div className={isInProgress ? "colored-ongoingCalls status-IN-PROGRESS" : "badge badge-primary"}>
            {text}
          </div>
        );
      },
    },
    {
      name: <ColumnToolTip data="Company Name" />,
      sortable: false,
      width: "170px",
      minWidth: "170px",
      selector: (row: VriOngoingView) => <ColumnToolTip data={row.companyName ?? "-"} />,
    },
    {
      name: <ColumnToolTip data="Unique Name" />,
      sortable: false,
      width: "160px",
      minWidth: "160px",
      selector: (row: VriOngoingView) => <ColumnToolTip data={row.uniqueName ?? "-"} />,
    },
    {
      name: <ColumnToolTip data="Language" />,
      sortable: false,
      width: "120px",
      minWidth: "120px",
      selector: (row: VriOngoingView) => <ColumnToolTip data={row.language ?? "-"} />,
    },
    {
      name: <ColumnToolTip data="Chat Sid" />,
      sortable: false,
      width: "150px",
      minWidth: "150px",
      selector: (row: VriOngoingView) => <ColumnToolTip data={row.chatSid ?? "-"} />,
    },
    {
      name: <ColumnToolTip data="Room Created" />,
      sortable: false,
      width: "120px",
      minWidth: "120px",
      selector: (row: VriOngoingView) => {
        const date = row.roomCreatedAt ? Number(row.roomCreatedAt) : null;
        return <ColumnToolTip data={date ? moment(date).format("HH:mm:ss") : "-"} />;
      },
    },
    {
      name: <ColumnToolTip data="Duration" />,
      sortable: false,
      width: "110px",
      minWidth: "110px",
      selector: (row: VriOngoingView) => (
        <LiveDurationCell startMs={row.roomCreatedAt ? Number(row.roomCreatedAt) : undefined} />
      ),
    },
  ];

  const vriTableStyles = {
    table: {
      style: {
        backgroundColor: "var(--surface)",
      },
    },
    tableWrapper: {
      style: {
        backgroundColor: "var(--surface)",
      },
    },
    head: {
      style: {
        backgroundColor: "var(--header-bg)",
        color: "var(--text)",
      },
    },
    headRow: {
      style: {
        backgroundColor: "var(--header-bg)",
        color: "var(--text)",
        borderColor: "var(--border)",
      },
    },
    headCells: {
      style: {
        fontWeight: "700",
        fontSize: "13px",
        color: "var(--text-muted)",
        backgroundColor: "var(--header-bg)",
        textTransform: "uppercase" as const,
      },
    },
    rows: {
      style: {
        backgroundColor: "var(--surface)",
        color: "var(--text)",
      },
    },
    cells: {
      style: {
        color: "var(--text)",
      },
    },
  };

  const VriOngoingAccordionRow: React.FC<{ data: VriOngoingView }> = ({ data }) => {
    const participants = data?.participants ?? [];
    const shouldUseTwoColumns = participants.length > 5;
    const leftColumnCount = shouldUseTwoColumns ? Math.ceil(participants.length / 2) : participants.length;
    const participantColumns = [
      participants.slice(0, leftColumnCount),
      participants.slice(leftColumnCount),
    ].filter((column) => column.length > 0);

    return (
      <div className="ongoing-calls-accordion py-3 px-4 border-top">
        <div style={{ marginLeft: "40px" }}>
          <div className="row">
            <div className="col-12 col-xs-12 col-lg-4">
              <sup className="text-muted">Room-SId: {data?.roomSid ?? "-"}</sup>
            </div>
            <div className="col-12 col-xs-12 col-lg-4">
              <sup className="text-muted">Call-Id: {data?.callId ?? "-"}</sup>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <div className="d-flex flex-column">
                <div>
                  <h6 style={{ marginBottom: "10px", fontSize: "15px" }}>Participants</h6>
                  {participants.length > 0 ? (
                    <div className="row">
                      {participantColumns.map((column, columnIndex) => (
                        <div
                          key={`participant-column-${columnIndex}`}
                          className={shouldUseTwoColumns ? "col-12 col-lg-4" : "col-12"}
                        >
                          <ul className="ongoing-calls-accordion__participant-list">
                            {column.map((participant, idx) => (
                              <li
                                key={`${participant.userId ?? "participant"}-${columnIndex}-${idx}`}
                                className="ongoing-calls-accordion__participant-item"
                              >
                                {participant.userName ?? "_"}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span>No participants</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

        const ENABLE_CANARY = true;
    
        useEffect(() => {
            if (!ENABLE_CANARY) return;
    
            const client = generateClient();
    
            const observable = client.graphql({
                query: Subscriptions.onOngoingCalls,
                // authMode: 'userPool' | 'apiKey' | 'iam' | 'oidc'  // if needed
            });
    
            // Type-safed canary subscriber
            const sub = (observable as any).subscribe({
                next: (ev: any) => console.log('[CANARY onOngoingCalls] event:', ev),
                error: (err: any) => console.error('[CANARY onOngoingCalls] error:', err),
                complete: () => console.warn('[CANARY onOngoingCalls] complete'),
            });
    
            return () => {
                try { sub.unsubscribe?.(); } catch { }
            };
        }, []);



  return (
    <>
      <LoadingBar
        progress={progress}
        onLoaderFinished={handleFinished}
      />

      <AdminPageTemplate
        pagingKey="PageOngoingCalls"
        loaderProgress={progress}
        showExportOptions={false}
        showFilter={false}
        hideSearchBar={true}
        pageBreadCrumbTile="Call Center"
        pageTitle="OPI Ongoing Calls"
        tableHeaders={ongoingCallsTableColumns}
        tableData={opiCalls || []}
        // pageIndex={page}
        pageSize={pageSize}
        // hasNextPage={!isLastPage}
        loading={loading}
        // onChangePageIndex={setPage}
        // onChangePageSize={(size: number) => { setPageSize(size); setPage(1); }}
        hasPagingData={hasPagingData}
        setHasPagingData={setHasPagingData}
        disablePaging = {disablePaging}
        pageName="ongoingCallsPage"
        tableType={ExportTableType.CallCenterStatusLog}
        callCenterId={callCenterId}
        showAutoRefresh={true}
        isAccordionEnabled={true}
        rowDetailsComponent={OngoingCallsAccordionRow}
        noDataFoundText={emptyMessage}
      />
      <div className="row">
        <div className="col-12 mt-3 mb-3">
          <div className="component-card ">
            <div className="component-card-header d-flex align-items-center justify-content-between border-top border-right border-left  ">
              <div>
                <span>VRI Ongoing Calls</span>
              </div>
            </div>
            <div className="component-card-body border-top border-bottom border-right border-left p-0">
              <DataTable
                columns={vriTableColumns}
                data={vriCalls || []}
                noDataComponent={<NoDataComponent title={vriEmptyMessage} />}
                customStyles={vriTableStyles}
                highlightOnHover={true}
                pointerOnHover={true}
                sortServer={false}
                expandableRows={true}
                expandableRowsComponent={VriOngoingAccordionRow}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
